import { FC, useMemo, useState } from 'react'
import classNames from 'classnames/bind'
import { FormBlock, Icon, Loader, RoundedButton } from '../../../shared'
import { getV3ColorCssVariable } from '../../../../design-system/colors_v3'
import { useGetUserSessions, useLogoutSessions } from '../../../../hooks/queries/userSessions'
import { UserSession, UserSessionDevice } from '../../../../model/UserSession'
import {
  getDeviceIpAddress,
  getDeviceLabel,
  getOtherSessionIds,
  isDeviceCurrent,
} from '../../../../misc/userSession.utilities'
import { LoginHistoryBottomPanel, SessionLogoutConfirmModal } from '../../../userSessions'
import { DeviceCard } from './DeviceCard'
import styles from './ConnectedDevicesBlock.module.scss'

const cx = classNames.bind(styles)

interface PendingLogout {
  sessionIds: string[]
  deviceLabel: string
  ipAddress?: string
  bulk: boolean
  successTitle: string
  successMessage?: string
}

/**
 * Bloc « Appareils connectés » pour la page Paramètres (Mon Compte).
 * Même pattern FormBlock que Assistant·e·s : chip + lien Historique.
 */
export const ConnectedDevicesBlock: FC = () => {
  const { query } = useGetUserSessions()
  const { mutate: logoutSessions, isPending: isLoggingOut } = useLogoutSessions()

  const [isHistoryOpen, setIsHistoryOpen] = useState(false)
  const [pendingLogout, setPendingLogout] = useState<PendingLogout | null>(null)

  const devices: UserSessionDevice[] = useMemo(() => {
    const list = query.data ?? []
    return [...list].sort((a, b) => Number(isDeviceCurrent(b)) - Number(isDeviceCurrent(a)))
  }, [query.data])
  const currentDevice = useMemo(() => devices.find(isDeviceCurrent), [devices])
  const otherSessionIds = useMemo(() => getOtherSessionIds(devices), [devices])
  const hasOtherSessions = otherSessionIds.length > 0
  const showBulkAction = !query.isLoading && !query.isError && hasOtherSessions

  const handleRequestLogoutSession = (session: UserSession, device: UserSessionDevice) => {
    const label = getDeviceLabel(device)
    setPendingLogout({
      sessionIds: [session.id],
      deviceLabel: label,
      ipAddress: session.ipAddress || getDeviceIpAddress(device),
      bulk: false,
      successTitle: 'Session déconnectée',
      successMessage: `Une session de ${label} a bien été déconnectée.`,
    })
  }

  const handleRequestLogoutAllOthers = () => {
    setPendingLogout({
      sessionIds: otherSessionIds,
      deviceLabel: currentDevice ? getDeviceLabel(currentDevice) : 'cet appareil',
      ipAddress: currentDevice ? getDeviceIpAddress(currentDevice) : undefined,
      bulk: true,
      successTitle: 'Appareils déconnectés',
      successMessage: 'Tous les autres appareils ont bien été déconnectés.',
    })
  }

  const handleConfirmLogout = () => {
    if (!pendingLogout) {
      return
    }
    logoutSessions(
      {
        sessionIds: pendingLogout.sessionIds,
        successTitle: pendingLogout.successTitle,
        successMessage: pendingLogout.successMessage,
      },
      { onSettled: () => setPendingLogout(null) },
    )
  }

  return (
    <>
      <FormBlock
        label="Appareils connectés"
        icon="desktop"
        linkLabel="Historique"
        onLinkClick={() => setIsHistoryOpen(true)}
      >
        <div className={cx('content')} data-test-id="connected_devices-block">
          <div className={cx('securityHint')} data-test-id="connected_devices-security_hint">
            <Icon
              icon="warningTriangle"
              size="normal"
              color={getV3ColorCssVariable('status', 'warning')}
            />
            <div className={cx('securityHintContent')}>
              <p className={cx('securityHintTitle')}>Vous ne reconnaissez pas un appareil ?</p>
              <p className={cx('securityHintText')}>
                Déconnectez-le et changez votre mot de passe.
              </p>
            </div>
          </div>

          {query.isLoading && (
            <div className={cx('stateCentered')} data-test-id="connected_devices-loading">
              <Loader width={64} height={64} />
            </div>
          )}

          {query.isError && (
            <div className={cx('stateError')} data-test-id="connected_devices-error">
              <Icon
                icon="warningTriangle"
                size="mega"
                color={getV3ColorCssVariable('shades', 'shade4')}
              />
              <div>
                <p className={cx('stateErrorTitle')}>
                  Impossible de charger vos appareils connectés
                </p>
                <p className={cx('stateErrorMessage')}>
                  Une erreur est survenue. Vérifiez votre connexion et réessayez.
                </p>
              </div>
              <RoundedButton
                label="Réessayer"
                theme="primary"
                icon="reload"
                onClick={() => query.refetch()}
                testId="button-connected_devices-retry"
              />
            </div>
          )}

          {!query.isLoading && !query.isError && (
            <>
              <div className={cx('deviceList')} data-test-id="connected_devices-list">
                {devices.map((device, index) => (
                  <DeviceCard
                    key={`${device.browser}-${device.os}-${device.device}-${index}`}
                    device={device}
                    actionsDisabled={isLoggingOut}
                    onRequestLogoutSession={handleRequestLogoutSession}
                  />
                ))}
                {!hasOtherSessions && (
                  <p
                    className={cx('singleSessionMessage')}
                    data-test-id="connected_devices-single_session"
                  >
                    Aucune autre session à déconnecter pour le moment.
                  </p>
                )}
              </div>
              {showBulkAction && (
                <div className={cx('bulkAction')}>
                  <RoundedButton
                    label="Déconnecter tous les autres appareils"
                    theme="invalid"
                    size="micro"
                    icon="power"
                    disabled={isLoggingOut}
                    onClick={handleRequestLogoutAllOthers}
                    testId="button-connected_devices-logout_all_others"
                  />
                </div>
              )}
            </>
          )}
        </div>
      </FormBlock>

      <SessionLogoutConfirmModal
        display={!!pendingLogout}
        deviceLabel={pendingLogout?.deviceLabel ?? ''}
        ipAddress={pendingLogout?.ipAddress}
        bulk={pendingLogout?.bulk ?? false}
        disableSubmit={isLoggingOut}
        onConfirm={handleConfirmLogout}
        onClose={() => setPendingLogout(null)}
      />
      <LoginHistoryBottomPanel
        display={isHistoryOpen}
        onRequestClose={() => setIsHistoryOpen(false)}
      />
    </>
  )
}
